import { jsx } from "react/jsx-runtime";
import { createContext, useState, useEffect, useContext } from "react";
const AuthContext = createContext({
  user: null,
  isAuthenticated: false,
  isLoading: true,
  login: async () => {
  },
  logout: async () => {
  },
  refreshUser: async () => {
  },
  updateUser: () => {
  }
});
const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
const AuthProvider = ({
  children,
  userDataKey = "user",
  componentNamespace
}) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    const initAuth = async () => {
      try {
        let userData = null;
        if (componentNamespace) {
          const componentOptions = window.Joomla?.getOptions?.(componentNamespace);
          if (componentOptions?.[userDataKey]) {
            userData = componentOptions[userDataKey];
          }
        }
        if (!userData) {
          const joomlaOptions = window.Joomla?.getOptions?.() || {};
          for (const key of Object.keys(joomlaOptions)) {
            if (key.startsWith("com_") && joomlaOptions[key]?.[userDataKey]) {
              userData = joomlaOptions[key][userDataKey];
              break;
            }
          }
        }
        if (!userData) {
          const joomlaOptions = window.Joomla?.getOptions?.() || {};
          if (joomlaOptions[userDataKey]) {
            userData = joomlaOptions[userDataKey];
          }
        }
        if (!userData) {
          userData = window.Joomla?.getOptions?.(userDataKey);
        }
        if (userData) {
          setUser(userData);
        }
      } catch (error) {
        console.error("AuthProvider: Error initializing auth:", error);
      } finally {
        setIsLoading(false);
      }
    };
    initAuth();
  }, [userDataKey, componentNamespace]);
  const updateUser = (userData) => {
    setUser(userData);
  };
  const refreshUser = async () => {
    try {
      const response = await fetch("/index.php?option=com_ajax&format=json&task=user.current", {
        method: "GET",
        headers: {
          "Content-Type": "application/json"
        }
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success && result.data) {
          setUser(result.data);
        } else {
          setUser(null);
        }
      } else {
        setUser(null);
      }
    } catch (error) {
      console.error("Error fetching user:", error);
      setUser(null);
    }
  };
  const login = async (credentials) => {
    try {
      const response = await fetch("/index.php?option=com_users&task=user.login&format=json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(credentials)
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          await refreshUser();
        } else {
          throw new Error(result.message || "Login failed");
        }
      } else {
        throw new Error("Login request failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };
  const logout = async () => {
    try {
      const token = window.Joomla?.getOptions?.("csrf.token") || "";
      const formData = new FormData();
      formData.append("option", "com_users");
      formData.append("task", "user.logout");
      formData.append("format", "json");
      if (token) {
        formData.append(token, "1");
      }
      const response = await fetch("/index.php", {
        method: "POST",
        body: formData
      });
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setUser(null);
          window.location.href = "/";
        } else {
          throw new Error(result.message || "Logout failed");
        }
      } else {
        throw new Error("Logout request failed");
      }
    } catch (error) {
      console.error("Logout error:", error);
      setUser(null);
      window.location.href = "/";
    }
  };
  const isAuthenticated = user !== null && !user.guest;
  return /* @__PURE__ */ jsx(
    AuthContext.Provider,
    {
      value: {
        user,
        isAuthenticated,
        isLoading,
        login,
        logout,
        refreshUser,
        updateUser
      },
      children
    }
  );
};
export {
  AuthProvider,
  useAuth
};
//# sourceMappingURL=auth-context.js.map
